import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import Card, { SectionHeading } from "../../components/Card";
import CoreLayout from "../../components/layout/CoreLayout";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import SetupFinishBanner from "../../components/SetupFinishBanner";
import { handleAuthHttpStatus, isAuthHttpStatus } from "../../lib/authErrors";
import { cardBorderClassName } from "../../lib/cardStyles";
import { type CallLog, parseCallLogDate, parseCallLogTotal, parseCallLogs } from "../../lib/callLogs";
import { fetchCallers, hydrateNames } from "../../lib/callers";
import { formatPhoneDisplay } from "../../lib/formatPhone";
import { applyOverlays, recentlyCompletedItems } from "../../lib/actionItemOverlays";
import { type ActionItem, ActionItemsHttpError, fetchActionItems } from "../../lib/actionItems";
import { shouldShowSetupBanner } from "../../lib/setupState";
import { usePageTitle } from "../../lib/usePageTitle";

// -----------------------------------------------------------------------------
// Backend API base (MUST be nginx/Flask, not Vercel)
// -----------------------------------------------------------------------------
const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

const PHONE_PROVISIONING_PLACEHOLDER =
  "We're setting up your dedicated phone number — you'll get an email within one business day with your number and simple instructions for forwarding your existing business line to it. Your customers keep calling the number they already know; your AI receptionist answers.";

const PHONE_FORWARDING_HINT =
  "Forward your existing business line to this number to put your receptionist to work. Forwarding instructions were included in your activation email.";

function GradientCard({ children }: { children: ReactNode }) {
  return (
    <Card padding="p-5" className="h-full flex flex-col">
      {children}
    </Card>
  );
}

function callerInitials(name: string | null): string | null {
  if (!name?.trim()) return null;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatRelativeFromDate(d: Date): string {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (dayStart.getTime() === startOfToday.getTime()) return "today";
  const diffDays = Math.floor(
    (startOfToday.getTime() - dayStart.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays > 0 && diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatActivityTime(d: Date): string {
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) return `today ${time}`;
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}, ${time}`;
}

function formatLastCallFineGrained(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const diffM = Math.floor(diffMs / (1000 * 60));
  const diffH = Math.floor(diffMs / (1000 * 60 * 60));
  const diffD = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffM < 60) return `${Math.max(diffM, 0)}m ago`;
  if (diffH < 24) return `${diffH}h ago`;
  return `${diffD}d ago`;
}

const tail10 = (s: string) => (s || "").replace(/\D/g, "").slice(-10);

function DeltaChip({ pct, direction }: { pct: number; direction: "up" | "down" }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
        direction === "up"
          ? "bg-green-500/15 text-green-400"
          : "bg-red-500/15 text-red-400"
      }`}
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {direction === "up" ? "▲" : "▼"} {pct}% vs last week
    </span>
  );
}

function KPITile({
  label,
  value,
  onClick,
  loading,
  subLabel,
  delta,
  navLabel,
}: {
  label: string;
  value: string | number;
  onClick?: () => void;
  loading?: boolean;
  subLabel?: string;
  delta?: { pct: number; direction: "up" | "down" };
  navLabel?: string;
}) {
  const className = `flex flex-col items-start justify-start rounded-lg p-4 text-left w-full ${cardBorderClassName}${
    onClick ? " cursor-pointer" : ""
  }`;
  const style = { background: "rgba(28,28,37,0.8)" };

  const content = (
    <>
      <span
        className="text-xs text-gray-500 uppercase tracking-wide"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {label}
      </span>
      {loading ? (
        <div
          className="h-8 w-16 mt-1 rounded bg-white/10 animate-pulse"
          aria-hidden
        />
      ) : (
        <>
          <p
            className="text-2xl font-semibold mt-1 text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            {value}
          </p>
          {subLabel && (
            <p
              className="text-xs text-gray-500 mt-0.5"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {subLabel}
            </p>
          )}
          {delta && (
            <div className="mt-1.5">
              <DeltaChip pct={delta.pct} direction={delta.direction} />
            </div>
          )}
        </>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        style={style}
        aria-label={navLabel ?? label}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={className} style={style}>
      {content}
    </div>
  );
}

export default function Dashboard() {
  usePageTitle("Dashboard");
  const [isLoading, setIsLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState<string>("");
  const [loadError, setLoadError] = useState<string | null>(null);

  const [config, setConfig] = useState<any>(null);
  const [calls, setCalls] = useState<CallLog[]>([]);
  const [callLogTotal, setCallLogTotal] = useState<number | null>(null);
  const [totalCallsLoading, setTotalCallsLoading] = useState(true);
  const [totalCallsError, setTotalCallsError] = useState(false);
  const [openItems, setOpenItems] = useState<ActionItem[] | null>(null);
  const [openItemsError, setOpenItemsError] = useState(false);
  const [showSetupBanner, setShowSetupBanner] = useState(false);
  const [phoneNameLookup, setPhoneNameLookup] = useState<Map<string, string>>(() => new Map());
  const callerNamesLoadId = useRef(0);

  const getCustomerId = () =>
    localStorage.getItem("customerId") || sessionStorage.getItem("customerId");

  useEffect(() => {
    const customerId = getCustomerId();
    if (customerId) {
      setShowSetupBanner(shouldShowSetupBanner(customerId));
    }
  }, []);

  const loadOpenItems = async (customerId: string) => {
    setOpenItemsError(false);
    try {
      setOpenItems(await fetchActionItems(customerId));
    } catch (e) {
      if (e instanceof ActionItemsHttpError && isAuthHttpStatus(e.status)) {
        void handleAuthHttpStatus(e.status, () => {});
      }
      setOpenItemsError(true);
    }
  };

  const loadCallerNames = async (customerId: string) => {
    const loadId = ++callerNamesLoadId.current;
    try {
      const { callers } = await fetchCallers(customerId);
      if (loadId !== callerNamesLoadId.current) return;
      const lookup = new Map<string, string>();
      for (const c of callers) {
        if (c.name) lookup.set(c.phone, c.name);
      }
      setPhoneNameLookup(lookup);
      const byCallCount = [...callers].sort((a, b) => b.call_count - a.call_count);
      const hydrated = await hydrateNames(customerId, byCallCount, 5);
      if (loadId !== callerNamesLoadId.current || hydrated.size === 0) return;
      setPhoneNameLookup((prev) => {
        const next = new Map(prev);
        hydrated.forEach((name, phone) => next.set(phone, name));
        return next;
      });
    } catch {
      // Names are optional; Top callers and Recent activity fall back to phone numbers.
    }
  };

  const loadAll = async () => {
    setIsLoading(true);
    setStatusMsg("");
    setLoadError(null);

    try {
      const customerId = getCustomerId();
      if (!customerId) {
        setStatusMsg("❌ No customer ID found. Please log in again.");
        setOpenItemsError(true);
        setIsLoading(false);
        return;
      }

      void loadOpenItems(customerId);
      void loadCallerNames(customerId);

      // Customer config (agent name + phone for status pill)
      const res = await fetch(`${API_BASE}/customers/${customerId}/config`, {
        method: "GET",
        credentials: "include",
      });

      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          setStatusMsg("❌ Unauthorized. Please log in again.");
        });
        setIsLoading(false);
        return;
      }

      if (res.ok) {
        const cfg = await res.json();
        setConfig(cfg);
      } else {
        const txt = await res.text().catch(() => "");
        setStatusMsg(`❌ Failed to load config: HTTP ${res.status} ${txt}`);
      }
    } catch (e: any) {
      setStatusMsg(`❌ Load error: ${e?.message || String(e)}`);
      setLoadError("Failed to load dashboard data. Please refresh or try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchTotalCalls = async () => {
      setTotalCallsLoading(true);
      setTotalCallsError(false);

      try {
        const customerId = getCustomerId();
        if (!customerId) {
          if (!cancelled) setTotalCallsError(true);
          return;
        }

        const res = await fetch(`${API_BASE}/customers/${customerId}/call-logs?limit=500`, {
          method: "GET",
          credentials: "include",
        });

        if (cancelled) return;

        if (isAuthHttpStatus(res.status)) {
          void handleAuthHttpStatus(res.status, () => {});
          setTotalCallsError(true);
          return;
        }

        if (!res.ok) {
          setTotalCallsError(true);
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          setCalls(parseCallLogs(data));
          setCallLogTotal(parseCallLogTotal(data));
        }
      } catch {
        if (!cancelled) setTotalCallsError(true);
      } finally {
        if (!cancelled) setTotalCallsLoading(false);
      }
    };

    void fetchTotalCalls();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Router bootstrap in this app injects a navigate helper.
  const nav = (path: string) => {
    if (typeof (window as any).REACT_APP_NAVIGATE === "function") {
      (window as any).REACT_APP_NAVIGATE(path);
      return;
    }
    // fallback: hard nav
    window.location.href = path;
  };

  const agentName = useMemo(() => {
    return (config?.agent?.agent_name || "").trim() || "your virtual receptionist";
  }, [config]);

  const assignedPhone = (config?.phone?.twilio_phone_number || "").trim();
  const phoneNumber = assignedPhone || PHONE_PROVISIONING_PLACEHOLDER;

  const openFollowUps = useMemo(
    () => applyOverlays(openItems ?? []).filter((item) => item.status === "open").length,
    [openItems],
  );

  const lastCallDate = useMemo(() => {
    const dates = calls.map((c) => parseCallLogDate(c.date)).filter(Boolean) as Date[];
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates.map((d) => d.getTime())));
  }, [calls]);

  const callWeekStats = useMemo(() => {
    const callDates = calls.map((c) => parseCallLogDate(c.date)).filter(Boolean) as Date[];
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const cut7 = new Date(startOfToday);
    cut7.setDate(cut7.getDate() - 6);
    const cut14 = new Date(startOfToday);
    cut14.setDate(cut14.getDate() - 13);
    const callsToday = callDates.filter((d) => d >= startOfToday).length;
    const callsThisWeek = callDates.filter((d) => d >= cut7).length;
    const callsPriorWeek = callDates.filter((d) => d >= cut14 && d < cut7).length;
    const wowPct =
      callsPriorWeek === 0
        ? null
        : Math.round(((callsThisWeek - callsPriorWeek) / callsPriorWeek) * 100);
    const avgPerDay = Math.round((callsThisWeek / 7) * 10) / 10;
    return { callsToday, callsThisWeek, wowPct, avgPerDay };
  }, [calls]);

  const dayBuckets = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dates = calls.map((c) => parseCallLogDate(c.date)).filter(Boolean) as Date[];
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const dayStart = new Date(startOfToday);
      dayStart.setDate(dayStart.getDate() - i);
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);
      const count = dates.filter((d) => d >= dayStart && d < dayEnd).length;
      out.push({
        label: dayStart.toLocaleDateString(undefined, { weekday: "short" }),
        count,
      });
    }
    return out;
  }, [calls]);

  const topCallers = useMemo(() => {
    const groups = new Map<string, { phone: string; count: number; lastCall: Date | null }>();
    for (const call of calls) {
      const key = tail10(call.caller);
      if (!key) continue;
      const parsed = parseCallLogDate(call.date);
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { phone: call.caller, count: 1, lastCall: parsed });
        continue;
      }
      existing.count += 1;
      if (parsed && (!existing.lastCall || parsed.getTime() > existing.lastCall.getTime())) {
        existing.lastCall = parsed;
      }
    }
    return Array.from(groups.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 3);
  }, [calls]);

  const activityFeed = useMemo(() => {
    const callEvents = calls
      .map((c) => {
        const date = parseCallLogDate(c.date);
        if (!date) return null;
        const displayName =
          phoneNameLookup.get(tail10(c.caller)) ?? formatPhoneDisplay(c.caller);
        return { type: "call" as const, date, caller: c.caller, displayName };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);

    const doneEvents = recentlyCompletedItems(20)
      .map((it) => {
        if (!it.completed_at) return null;
        const date = new Date(it.completed_at);
        if (Number.isNaN(date.getTime())) return null;
        return { type: "done" as const, date, text: it.text, id: it.id };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);

    return [...callEvents, ...doneEvents]
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .slice(0, 6);
  }, [calls, phoneNameLookup]);

  const statusBg =
    statusMsg.startsWith("❌") ? "rgba(239,68,68,0.2)" :
    statusMsg.startsWith("✅") ? "rgba(34,197,94,0.2)" :
    "rgba(255,255,255,0.03)";

  const statusColor =
    statusMsg.startsWith("❌") ? "#fca5a5" :
    statusMsg.startsWith("✅") ? "#86efac" :
    "#d1d5db";

  return (
    <CoreLayout>
      <PageContainer>
        {showSetupBanner && getCustomerId() && (
          <SetupFinishBanner
            customerId={getCustomerId()!}
            onDismiss={() => setShowSetupBanner(false)}
          />
        )}
        <div className="mb-8">
          <PageHeader
            title="Dashboard"
            subtitle="Your AI system at a glance."
            actions={
              <button
                type="button"
                onClick={loadAll}
                disabled={isLoading}
                className="text-gray-500 hover:text-purple-400 transition-colors cursor-pointer disabled:opacity-50 p-0 bg-transparent border-0"
                aria-label="Reload dashboard"
              >
                <i className={`ri-refresh-line text-lg ${isLoading ? "animate-spin" : ""}`} aria-hidden />
              </button>
            }
          />

          <div className="flex flex-wrap gap-3 mb-8">
            {[
              {
                id: "ai",
                icon: "ri-robot-line",
                label: "AI Active",
                color: "text-purple-400",
                onClick: () => nav("/transfer"),
              },
              {
                id: "agent",
                icon: "ri-phone-line",
                label: `${agentName} · ${phoneNumber}`,
                color: "text-orange-400",
                onClick: () => nav("/phone-system-app?tab=agent"),
              },
              {
                id: "memory",
                icon: "ri-brain-line",
                label: "Memory Online",
                color: "text-purple-400",
                onClick: () => nav("/user-memories"),
              },
            ].map(({ id, icon, label, color, onClick }) => (
              <button
                key={id}
                type="button"
                onClick={onClick}
                className={[
                  "group flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer bg-transparent transition-colors hover:border-[rgba(138,43,226,0.35)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400",
                  id === "agent" && !assignedPhone
                    ? "max-w-lg whitespace-normal text-left rounded-xl items-start py-2"
                    : "",
                ].join(" ")}
                style={{
                  background: "rgba(28,28,37,0.8)",
                  border: "1px solid rgba(138,43,226,0.2)",
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                <i className={`${icon} ${color} text-xs`} aria-hidden />
                <span className="text-gray-300 group-hover:text-gray-100 group-hover:underline underline-offset-2">
                  {label}
                </span>
              </button>
            ))}
          </div>
          {assignedPhone ? (
            <p
              className="text-xs text-gray-400 mb-8 leading-relaxed max-w-2xl"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              {PHONE_FORWARDING_HINT}
            </p>
          ) : null}
        </div>

        {statusMsg && (
          <div
            className="mb-6 p-4 rounded-lg"
            style={{
              background: statusBg,
              border: "1px solid rgba(138,43,226,0.15)",
              color: statusColor,
            }}
          >
            {statusMsg}
          </div>
        )}

        {loadError && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-3">
            <i className="ri-error-warning-line text-lg" />
            <span>{loadError}</span>
            <button
              onClick={() => {
                setLoadError(null);
                loadAll();
              }}
              className="ml-auto text-xs underline hover:text-red-300"
            >
              Try again
            </button>
          </div>
        )}

          {/* TODO: add quality KPIs (avg duration, resolution rate, sentiment, AI-answered, transfers) once the post-call pipeline writes per-call fields. Do NOT render them until real — empty tiles read as broken. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            <KPITile
              label="Calls today"
              value={totalCallsError ? "—" : callWeekStats.callsToday}
              loading={totalCallsLoading}
              onClick={() => nav("/call-logs")}
              navLabel="Open Call Logs"
            />
            <KPITile
              label="Calls this week"
              value={totalCallsError ? "—" : callWeekStats.callsThisWeek}
              loading={totalCallsLoading}
              delta={
                callWeekStats.wowPct !== null
                  ? {
                      pct: Math.abs(callWeekStats.wowPct),
                      direction: callWeekStats.wowPct >= 0 ? "up" : "down",
                    }
                  : undefined
              }
              onClick={() => nav("/call-logs")}
              navLabel="Open Call Logs"
            />
            <KPITile
              label="Total calls"
              value={totalCallsError ? "—" : (callLogTotal ?? calls.length)}
              loading={totalCallsLoading}
              onClick={() => nav("/call-logs")}
              navLabel="Open Call Logs"
            />
            <KPITile
              label="Open follow-ups"
              value={openItemsError ? "—" : openFollowUps}
              loading={openItems === null && !openItemsError}
              onClick={() => nav("/open-items")}
              navLabel="Open Items"
            />
            <KPITile
              label="Last call"
              value={
                totalCallsError
                  ? "—"
                  : lastCallDate
                    ? formatLastCallFineGrained(lastCallDate)
                    : "No calls yet"
              }
              loading={totalCallsLoading}
              onClick={() => nav("/call-logs")}
              navLabel="Open Call Logs"
            />
          </div>

          <div className="mb-8">
            <GradientCard>
              <div className="flex items-center justify-between gap-3 mb-4">
                <SectionHeading className="!mb-0">Call volume</SectionHeading>
                <div className="flex items-center gap-2 shrink-0">
                  <span
                    className="text-xs text-gray-400"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {callWeekStats.callsThisWeek} this week
                  </span>
                  {callWeekStats.wowPct !== null && (
                    <DeltaChip
                      pct={Math.abs(callWeekStats.wowPct)}
                      direction={callWeekStats.wowPct >= 0 ? "up" : "down"}
                    />
                  )}
                </div>
              </div>
              {callWeekStats.callsThisWeek === 0 ? (
                <p
                  className="text-sm text-gray-500 text-center py-8"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  No calls in the last 7 days
                </p>
              ) : (
                <div className="flex gap-2 h-32">
                  {dayBuckets.map((bucket) => {
                    const max = Math.max(...dayBuckets.map((b) => b.count), 1);
                    const barPct = max > 0 ? (bucket.count / max) * 100 : 0;
                    return (
                      <div
                        key={bucket.label}
                        className="flex-1 flex flex-col items-center min-w-0 h-full"
                      >
                        <span
                          className="text-xs text-gray-500 mb-1"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {bucket.count}
                        </span>
                        <div className="flex-1 w-full flex flex-col justify-end">
                          {bucket.count > 0 && (
                            <div
                              className="w-full bg-[#8a2be2] rounded-t"
                              style={{
                                height: `${barPct}%`,
                                minHeight: 4,
                              }}
                            />
                          )}
                        </div>
                        <span
                          className="text-xs text-gray-500 mt-1"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {bucket.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </GradientCard>
          </div>

          <div className="mb-8">
            <GradientCard>
              <div className="flex items-center justify-between gap-3 mb-4">
                <SectionHeading className="!mb-0">Top callers</SectionHeading>
                <button
                  type="button"
                  onClick={() => nav("/user-memories")}
                  className="text-xs text-gray-500 hover:text-purple-400 transition-colors cursor-pointer"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  View all
                </button>
              </div>
              {topCallers.length === 0 ? (
                <p
                  className="text-sm text-gray-500 text-center py-6"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  No callers yet
                </p>
              ) : (
                <div className="space-y-2">
                  {topCallers.map((caller) => {
                    const resolvedName = phoneNameLookup.get(tail10(caller.phone)) ?? null;
                    const initials = callerInitials(resolvedName);
                    return (
                      <button
                        key={caller.phone}
                        type="button"
                        onClick={() =>
                          nav(`/user-memories?phone=${encodeURIComponent(caller.phone)}`)
                        }
                        className={`w-full flex items-center justify-between gap-3 p-3 rounded-lg text-left cursor-pointer hover:bg-white/5 transition-colors ${cardBorderClassName}`}
                        style={{ background: "rgba(13, 13, 18, 0.4)" }}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white"
                            style={{
                              background: "linear-gradient(135deg, #8a2be2, #ff6a00)",
                            }}
                          >
                            {initials ?? (
                              <i className="ri-phone-line text-base" aria-hidden />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p
                              className="text-sm font-semibold text-white truncate"
                              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                              {resolvedName || formatPhoneDisplay(caller.phone)}
                            </p>
                            <p
                              className="text-xs text-gray-500 mt-0.5"
                              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            >
                              {caller.count}{" "}
                              {caller.count === 1 ? "call" : "calls"} · last{" "}
                              {caller.lastCall
                                ? formatRelativeFromDate(caller.lastCall)
                                : "—"}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </GradientCard>
          </div>

          <div className="mb-8">
            <GradientCard>
              <SectionHeading>Recent activity</SectionHeading>
              {activityFeed.length === 0 ? (
                <p
                  className="text-sm text-gray-500 text-center py-6"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  No recent activity
                </p>
              ) : (
                <div className="space-y-2">
                  {activityFeed.map((event) => {
                    const relativeTime = formatActivityTime(event.date);
                    if (event.type === "call") {
                      return (
                        <button
                          key={`call-${event.caller}-${event.date.getTime()}`}
                          type="button"
                          onClick={() =>
                            nav(`/user-memories?phone=${encodeURIComponent(event.caller)}`)
                          }
                          className={`w-full flex items-center gap-3 p-3 rounded-lg text-left cursor-pointer hover:bg-white/5 transition-colors ${cardBorderClassName}`}
                          style={{ background: "rgba(13, 13, 18, 0.4)" }}
                        >
                          <i
                            className="ri-phone-incoming-line text-lg text-purple-400 shrink-0"
                            aria-hidden
                          />
                          <p
                            className="flex-1 min-w-0 text-sm text-white truncate"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            Call from {event.displayName}
                          </p>
                          <span
                            className="shrink-0 text-xs text-gray-500"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          >
                            {relativeTime}
                          </span>
                        </button>
                      );
                    }
                    const truncatedText =
                      event.text.length > 50 ? `${event.text.slice(0, 50)}…` : event.text;
                    return (
                      <button
                        key={`done-${event.id}`}
                        type="button"
                        onClick={() => nav("/open-items")}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg text-left cursor-pointer hover:bg-white/5 transition-colors ${cardBorderClassName}`}
                        style={{ background: "rgba(13, 13, 18, 0.4)" }}
                      >
                        <i
                          className="ri-checkbox-circle-line text-lg text-green-400 shrink-0"
                          aria-hidden
                        />
                        <p
                          className="flex-1 min-w-0 text-sm text-white truncate"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                          title={event.text}
                        >
                          Completed: {truncatedText}
                        </p>
                        <span
                          className="shrink-0 text-xs text-gray-500"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {relativeTime}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </GradientCard>
          </div>
      </PageContainer>
    </CoreLayout>
  );
}
